import generateFilterConfigs from "../../../utils/FilterConfigGenerator";
import React, { useContext, useEffect, useState } from "react";
import FilterComponent from "../../../components/complex/crud/FilterComponent";
import { Paginator } from "primereact/paginator";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";

import EditorDialog from "./JenisMinyakEditorDialog";
import { useDispatch } from "react-redux";
import UiContext from "../../../store/ui-context";
import useAPIRequest from "../../../custom_hooks/simple/useAPIRequest";
import { setLayoutDashboardTitle } from "../../../store/ui-slice";
import { DataTable } from "primereact/datatable";
import {
  genTemplateBodyAction,
  genTemplateBodyBooleanStatus,
  genTemplateBodyNumber,
  genTemplateBodyText,
} from "../../../components/utils/TableTemplateBodyGenerator";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import createFilterText from "../../../utils/CreateFilterText";

const title = "Master Jenis Minyak";
const fetchOrderBy = "nama";
const fetchOrderMethod = "ASC";
const apiUrl = "jenisminyak";

const editorDefaultValue = { nama: "" };

/**
 * Generate Filter Component
 *
 * available options for type:
 * text_plain
 * number_plain
 * date
 */
const filterConfigs = generateFilterConfigs([
  {
    name: "Nama",
    field: "nama",
    type: "text_plain",
    filter: true,
    filter_default: true,
  },
]);

const JenisMinyakMasterPage = (props, { className }) => {
  const uiContext = useContext(UiContext);
  const dispatch = useDispatch();
  const { requestGet, requestGetOne, requestPost, requestDelete, loading } =
    useAPIRequest();

  const { loading: editorLoading, requestPost: editorRequestPost } =
    useAPIRequest();

  const [firstLoad, setFirstLoad] = useState(false);
  const [paging, setPaging] = useState({
    page: 1,
    take: 10,
    filter: "",
    total: 0,
  });
  const [listData, setListData] = useState([]);
  const [selectedData, setSelectedData] = useState([]);

  const [addNewMode, setAddNewMode] = useState(false);
  const [editorData, setEditorData] = useState({});
  const [editorError, setEditorError] = useState({});
  const [editorDialogShowed, setEditorDialogShowed] = useState(false);

  // useEffect section

  useEffect(() => {
    dispatch(setLayoutDashboardTitle({ title }));
    fetchListData({ page: 1, take: 10, filter: "" });
  }, []);

  // API call section

  const fetchListData = async ({ page, take, filter }) => {
    if (loading) return;

    const _filter = filter;

    return requestGet({
      fullUrl: `/api/masterdata/${apiUrl}/data`,
      params: {
        page: page,
        take: take,
        filter: _filter,
        order: fetchOrderBy,
        order_method: fetchOrderMethod,
      },
      onSuccess: ({ pagination, message, data }) => {
        setPaging({
          ...paging,
          total: pagination["total"],
          page: pagination["page"],
          take: pagination["take"],
          filter: _filter,
        });
        setListData(data);
        if (!firstLoad) {
          setFirstLoad(true);
        }
      },
      onError: ({ message, data }) => uiContext.showErrorMsg("Error", message),
    });
  };

  const fetchData = async ({ id }) => {
    if (loading) return;
    return requestGetOne({
      fullUrl: `/api/masterdata/${apiUrl}/one/${id}`,
      onSuccess: ({ message, data }) => { },
      onError: ({ message, data }) => uiContext.showErrorMsg("Error", message),
    });
  };

  const deleteData = async ({ ids }) => {
    if (loading) return;

    return requestDelete({
      fullUrl: `/api/masterdata/${apiUrl}/delete`,
      ids: ids,
      onSuccess: ({ message, data }) => {
        const newListData = listData.filter((el) => !ids.includes(el.id));
        setListData(newListData);
        uiContext.showSuccessMsg("Success", message);
      },
      onError: ({ message, data }) => uiContext.showErrorMsg("Error", message),
    });
  };

  const saveData = async ({ data }) => {
    resetEditorError();
    if (loading) return;
    const valid = validateData();

    if (valid) {
      const body = convertSaveData(data);

      return editorRequestPost({
        fullUrl: `/api/masterdata/${apiUrl}/save`,
        body,
        isForm: false,
        onError: ({ message, data }) => {
          uiContext.showErrorMsg("Error", message);
          setEditorError(data);
        },
        onSuccess: ({ data, message }) => {
          uiContext.showSuccessMsg("Success", message);
        },
      });
    }
    return null;
  };

  // methods & functions section

  const resetEditorError = () => {
    setEditorError({});
  };

  const validateData = () => {
    let valid = true;
    return valid;
  };

  const convertSaveData = (data) => {
    return data;
  };

  // handle UI events section

  const handleOnReload = async () => {
    setPaging({ ...paging, filter: "" });
    fetchListData({
      page: paging.page,
      take: paging.take,
      filter: "",
    });
  };

  const handleOnFilter = (filterMap) => {
    const filterString = createFilterText(filterMap);
    setPaging({ ...paging, filter: filterString });
    fetchListData({
      page: paging.page,
      take: paging.take,
      filter: filterString,
    });
  };

  const handleOnPage = (e) => {
    const newPage = e.first / paging.take + 1;
    const newTake = e.rows;

    setPaging({ ...paging, take: newTake, page: newPage });
    fetchListData({ page: newPage, take: newTake, filter: paging.filter });
  };

  const handleOnDelete = (row) => {
    resetEditorError();
    const accept = async () => {
      if (loading) return;

      await deleteData({ ids: [row.id] });
    };
    const reject = () => { };

    confirmDialog({
      message: "Menghapus 1 data, lanjutkan?",
      header: "Konfirmasi",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept,
      reject,
    });
  };

  const handleDeleteMultiple = () => {
    resetEditorError();

    if (selectedData.length > 0) {
      const accept = async () => {
        if (loading) return;

        const listIds = selectedData.map((el) => el.id);
        await deleteData({ ids: listIds });
        setSelectedData([]);
      };
      const reject = () => { };

      confirmDialog({
        message: `Menghapus ${selectedData.length} data, lanjutkan?`,
        header: "Konfirmasi",
        icon: "pi pi-exclamation-triangle",
        acceptClassName: "p-button-danger",
        accept,
        reject,
      });
    }
  };

  const handleOnAddNew = () => {
    setAddNewMode(true);
    resetEditorError();
    setEditorData({ ...editorDefaultValue });
    setEditorDialogShowed(true);
  };

  const handleOnEdit = async (row) => {
    setAddNewMode(false);
    resetEditorError();
    const newData = await fetchData({ id: row.id });
    if (newData) {
      setEditorData({ ...newData });
      setEditorDialogShowed(true);
    }
  };

  const handleConfirmEditor = async (data) => {
    const newData = await saveData({ data });
    if (newData) {
      if (addNewMode) {
        setListData([newData, ...listData]);
      } else {
        const index = listData.findIndex((el) => el.id === newData.id);
        const newListData = [...listData];
        newListData.splice(index, 1, newData);
        setListData(newListData);
      }
      setEditorDialogShowed(false);
    }
  };

  // UI section

  /**
   * Generate Table Column
   *
   * various function to generate
   * genTemplateBodyAction({ row, onEdit, onDelete })
   * genTemplateBodyText({ row, field, defaultEmpty })
   * genTemplateBodyNumber({ row, field, defaultEmpty, maxFraction, minFraction })
   * genTemplateBodyDate({ row, field, format })
   * genTemplateBodyImage({ row, field })
   * genTemplateBodyBooleanStatus = ({ row, statusLabelMap = { 1: "Online", 0: "Offline" }, statusSeverityMap = { 1: "success", 0: "danger", default: "secondary" },})
   */
  const actionBodyTemplate = (row) =>
    genTemplateBodyAction({
      row,
      onEdit: (row) => handleOnEdit(row),
      onDelete: handleOnDelete,
    });

  // const kodeBodyTemplate = (row) => genTemplateBodyText({ row, field: "kode" });
  const namaBodyTemplate = (row) => genTemplateBodyText({ row, field: "nama" });
  const hargaBodyTemplate = (row) =>
    genTemplateBodyNumber({ row, field: "harga_per_liter", defaultEmpty: "0" });
  const statusBodyTemplate = (row) =>
    genTemplateBodyBooleanStatus({
      row,
      statusLabelMap: { 1: "Aktif", 0: "Tidak Aktif" },
      statusSeverityMap: { 1: "success", 0: "danger" },
    });
  const keteranganBodyTemplate = (row) => genTemplateBodyText({ row, field: "keterangan" });


  return (
    <div className={`col-start-start p-4 gap-4 w-100`}>
      <ConfirmDialog />
      <EditorDialog
        data={editorData}
        setData={setEditorData}
        addNewMode={addNewMode}
        loading={editorLoading}
        error={editorError}
        setError={setEditorError}
        visible={editorDialogShowed}
        onHide={() => setEditorDialogShowed(false)}
        onConfirm={handleConfirmEditor}
      />
      <div className="elevated-card-tight-vertical w-100">
        <FilterComponent onFilter={handleOnFilter} configs={filterConfigs} />
      </div>
      <div className="elevated-card col-center-center w-100 gap-3">
        <div className={`row-between-center gap-2 w-100`}>
          <div className="row-start-center gap-2">
            <Button
              icon="pi pi-plus"
              label="Tambah Baru"
              onClick={handleOnAddNew}
            />
            <Button
              disabled={selectedData.length === 0}
              icon="pi pi-trash"
              severity="danger"
              text
              label="Hapus"
              onClick={handleDeleteMultiple}
            />
          </div>
          <div className="row-start-center">
            <Button
              rounded
              outlined
              icon="pi pi-refresh"
              onClick={handleOnReload}
              style={{ width: "40px", height: "40px" }}
            />
          </div>
        </div>

        <div className={`row-center-center gap-2 w-100`}>
          <DataTable
            dataKey="id"
            className="w-100"
            loading={loading}
            showGridlines
            stripedRows
            editMode="cell"
            value={listData}
            selection={selectedData}
            onSelectionChange={(e) => setSelectedData(e.value)}
          >
            <Column headerClassName="w-[3rem]" selectionMode={"multiple"} />

            <Column
              header="Action"
              alignHeader={"center"}
              headerClassName="w-[9rem]"
              body={actionBodyTemplate}
            />
            <Column field="nama" header="Nama" body={namaBodyTemplate} />
            <Column
              field="harga_per_liter"
              header="Harga per Liter"
              headerClassName="w-[10rem] text-right"
              bodyClassName="text-right"
              body={hargaBodyTemplate}
            />
            <Column
              field="status"
              header="Status"
              headerClassName="w-[10rem]"
              body={statusBodyTemplate}
            />
            {/* <Column
              field="keterangan"
              header="Keterangan"
              headerClassName="w-[15rem]"
              body={keteranganBodyTemplate}
            /> */}
          </DataTable>
        </div>

        <Paginator
          first={parseInt(paging.take) * (paging.page - 1)}
          rows={parseInt(paging.take)}
          totalRecords={parseInt(paging.total)}
          rowsPerPageOptions={[5, 10, 20, 50]}
          onPageChange={handleOnPage}
        />
      </div>
    </div>
  );
};

export default JenisMinyakMasterPage;
